import { Component, Inject, OnInit } from '@angular/core';
import { Category } from '../../../../../../models/entities/accounting/category';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CategoriesService } from '../../../../../../services/accounting/categories.service';
import { MatDialogSizes } from '../../../../../../enums/mat-dialog-sizes.enum';
import { LegFiJwtService } from '../../../../../../services/auth/legfi-jwt.service';
import { map } from 'rxjs/operators';
import { validateRecursive } from '../../../../../../directives/validators/validate-recursive';
import { Accounting } from '../../../../../shared/http/request-interfaces';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogModule, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { SharedModule } from '../../../../../shared/shared.module';
import { FieldErrorDisplayComponent } from '../../../../../shared/field-error-display/field-error-display.component';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { GrowlerService } from '../../../../../shared/growler/growler.service';
import { ControlMessagesComponent } from '../../../../../shared/control-messages/control-messages.component';
import { CurrencyInputComponent } from '../../../../../shared/currency-input/currency-input.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { StringValidatorsModule } from '../../../../../../directives/validators/string/string-validators.module';
import moment from 'moment/moment';


export interface CategoryEditorDialogInput
{
    category?: Category;
    categoryId?: number;
    type?: 'income' | 'expense';
    isNew: boolean;
    parentCategoryId?: number;
}

export interface CategoryEditorDialogResult
{
    success: boolean;
    category?: Category;
}

@Component({
    selector: 'app-category-editor-dialog',
    standalone: true,
    templateUrl: './category-editor-dialog.component.html',
    styleUrls: ['./category-editor-dialog.scss'],
    imports: [
        SharedModule,
        FieldErrorDisplayComponent,
        MatLegacyDialogModule,
        MatLegacyInputModule,
        MatLegacySelectModule,
        ControlMessagesComponent,
        CurrencyInputComponent,
        MatDatepickerModule,
        StringValidatorsModule,
    ],
})
export class CategoryEditorDialogComponent implements OnInit
{
    form: UntypedFormGroup;
    submitInFlight: boolean = false;
    categoryLevel: string = this.data.parentCategoryId ? 'Subcategory' : 'Category';
    parentCategories: Category[] = [];
    allCategories: Category[] = [];
    isEditingSelf: boolean = false;

    constructor(
            private _builder: UntypedFormBuilder,
            private _categoriesService: CategoriesService,
            private _dialogRef: MatDialogRef<CategoryEditorDialogComponent>,
            private _growler: GrowlerService,
            @Inject(MAT_DIALOG_DATA) public data: CategoryEditorDialogInput,
    ) {
    }

    ngOnInit() {
        this._dialogRef.updateSize(MatDialogSizes.SM);

        const orgId = LegFiJwtService.read().orgId;
        this._categoriesService.getOrganizationStreams(orgId).$categories.pipe(map((cats: Category[]) => {
            this.allCategories = cats;
            if (!this.data.category && this.data.categoryId) {
                const foundCategory = cats.find(cat => cat.id === this.data.categoryId);
                if (foundCategory) {
                    this.data.category = foundCategory;
                    if (typeof this.data.parentCategoryId === 'undefined') {
                        // we probably opened the editor with just an id, fill the other required fields
                        this.data.parentCategoryId = foundCategory.parentId || null;
                        this.data.type = foundCategory.type;
                    }
                } else {
                    this._growler.oops(`Unable to find the category with ID ${this.data.categoryId}.`);
                    this.close({success: false});
                    return [];
                }
            }
            return cats.filter(cat => {
                return cat.type === this.data.type && cat.id !== this.data.category?.id && !cat.parentId && !cat.deletedAt;
            });
        })).subscribe((cats: Category[]) => {
            this.parentCategories = cats;
            this.buildModel();
        });
        this.buildModel();
    }

    buildModel() {
        this.form = this._builder.group({
            categoryName: [
                this.data.category?.name || '',
                [
                    Validators.required,
                    this.categoryNameValidator(),
                ],
            ],
            parentCategoryId: [
                this.data.parentCategoryId ? this.data.parentCategoryId : 0,
                [Validators.required],
            ],
            startingBalance: [
                this.data.category?.startingBalance || 0,
                Validators.required,
            ],
            asOf: [
                moment(this.data.category?.startingBalanceDate) || null,
                Validators.required,
            ],
        });

        if (this.data.category.children.length > 0) {
            this.form.get('parentCategoryId')?.disable();
        }

        // Trigger category name validation whenever parentCategoryId selection changes
        this.form.get('parentCategoryId')?.valueChanges.subscribe(() => {
            this.form.get('categoryName')?.updateValueAndValidity();
        });
    }

    categoryNameValidator() {
        return (formControl: UntypedFormControl) => {
            if (formControl && this.form) {
                const newCategoryName: string = this.form.get('categoryName')?.value.trim();
                const parentCategoryId: number = this.form.get('parentCategoryId')?.value;


                const nameOccurrences: number = this.allCategories.filter((cat: Category) => {
                    if (this.data.category?.id === cat.id) {
                        return false; // dont check against itself in the list on edit
                    }
                    if (parentCategoryId && cat.parentId !== parentCategoryId) {
                        return false; // only check against the same parent category
                    }
                    return cat.name.toLowerCase().trim() === newCategoryName.toLowerCase();
                }).length;
                if (newCategoryName === '') {
                    return {categoryNameError: `* The category name must contain at least one non-space character.`};
                }
                if (nameOccurrences > 0) {
                    if (parentCategoryId > 0) {
                        return {categoryNameError: `* Subcategory names must be unique per category.`};
                    } else {
                        return {categoryNameError: `* Category names must be unique.`};
                    }
                }
                return null;
            }
            return null;
        };
    }

    close(result: CategoryEditorDialogResult) {
        this._dialogRef.close(result);
    }

    onSubmit(): void {
        validateRecursive(this.form);
        if (this.form.invalid) {
            this._growler.oops(`Unable to submit the form. Make sure all fields are valid.`);
            return;
        }
        this.submitInFlight = true;
        this.data.isNew ? this.create() : this.update();
    }

    create(): void {
        const newCategoryLevel = this.form.get('parentCategoryId').value > 0 ? 'subcategory' : 'category';
        const requestBody: Accounting.Categories.StoreCategoryRequest = {
            type: this.data.type,
            name: this.form.get('categoryName').value.trim(),
            parentId: this.form.get('parentCategoryId').value > 0 ? this.form.get('parentCategoryId').value : null,
            asOf: this.form.get('asOf').value.format('YYYY-MM-DD'),
            startingBalance: this.form.get('startingBalance').value || 0,
        };

        this._categoriesService.createCategory(null, requestBody).subscribe({
            next: () => {
                this._growler.success('Success', 'Your new category was successfully created.');
                // category isn't important, parent will refresh
                this.close({success: true, category: null});
            },
            error: () => {
                this._growler.oops(`Unable to create new ${newCategoryLevel}.`);
                this.submitInFlight = false;
            },
        });

    }

    update(): void {
        const newCategoryLevel = this.form.get('parentCategoryId').value > 0 ? 'subcategory' : 'category';
        this._categoriesService
                .updateCategory(null, this.data.category.id, {
                    categoryId: this.data.category.id,
                    name: this.form.get('categoryName').value.trim(),
                    parentId: this.form.get('parentCategoryId').value > 0
                            ? this.form.get('parentCategoryId').value
                            : null,
                    sortOrder: this.data.category.sortOrder,
                    asOf: this.form.get('asOf').value.format('YYYY-MM-DD'),
                    startingBalance: this.form.get('startingBalance').value || 0,
                })
                .subscribe({
                    next: () => {
                        this.data.category.name = this.form.get('categoryName').value;
                        this._growler.success('Success', `Your ${newCategoryLevel} was successfully updated.`);
                        this.close({
                            success: true,
                            category: this.data.category,
                        });
                    },
                    error: () => {
                        this._growler.oops(`Unable to update the ${newCategoryLevel}.`);
                        this.submitInFlight = false;
                    },
                });
    }

    isFieldValid(controlName: string) {
        const control = this.form.get(controlName);
        if (!control.valid && control.touched) {
            return control;
        }
    }
}
